import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useToast } from '../../components/ui/Toast';
import { friendlyError } from '../../utils/errorMessages';
import { supabase } from '../../lib/supabase';

const OAuthIntegrationSetup = () => {
  const { user, userProfile, updateProfile } = useAuth();
  const toast = useToast();
  const [connections, setConnections] = useState({
    linkedin: { connected: false, lastAuth: null, status: 'disconnected', tokenExpiry: null },
    twitter: { connected: false, lastAuth: null, status: 'disconnected', tokenExpiry: null }
  });
  const [loading, setLoading] = useState({});

  useEffect(() => {
    loadConnectionStatus();
  }, [userProfile]);

  const loadConnectionStatus = () => {
    if (userProfile?.linkedin_access_token) {
      setConnections(prev => ({
        ...prev,
        linkedin: {
          connected: true,
          lastAuth: userProfile?.created_at,
          status: 'connected',
          tokenExpiry: userProfile?.linkedin_token_expires_at
        }
      }));
    }
    if (userProfile?.twitter_access_token) {
      setConnections(prev => ({
        ...prev,
        twitter: {
          connected: true,
          lastAuth: userProfile?.created_at,
          status: 'connected',
          tokenExpiry: userProfile?.twitter_token_expires_at
        }
      }));
    }
  };

  const handleConnect = async (provider) => {
    if (!user) {
      toast.error('Please sign in to connect social accounts.');
      return;
    }

    setLoading(prev => ({ ...prev, [provider]: true }));

    try {
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();

      if (sessionError || !session || !session?.access_token) {
        toast.error('Your session has expired. Please sign in again.');
        setLoading(prev => ({ ...prev, [provider]: false }));
        return;
      }

      const clientId = (provider === 'linkedin'
        ? import.meta.env?.VITE_LINKEDIN_CLIENT_ID
        : import.meta.env?.VITE_TWITTER_CLIENT_ID)?.trim();

      if (!clientId || clientId === 'your-linkedin-client-id-here' || clientId === 'your-twitter-client-id-here') {
        toast.warning(`${provider === 'linkedin' ? 'LinkedIn' : 'X'} integration is not configured yet.`);
        setLoading(prev => ({ ...prev, [provider]: false }));
        return;
      }

      const randomState = Math.random()?.toString(36)?.substring(7) + Date.now()?.toString(36);
      const state = `${provider}:${randomState}`;
      sessionStorage.setItem(`oauth_state_${provider}`, state);
      sessionStorage.setItem(`oauth_user_id_${provider}`, user?.id);

      const redirectUri = `${window.location.origin}/oauth-callback`;
      let authUrl;

      if (provider === 'linkedin') {
        const scope = 'openid profile email w_member_social';
        authUrl = `https://www.linkedin.com/oauth/v2/authorization?` +
          `response_type=code&` +
          `client_id=${encodeURIComponent(clientId)}&` +
          `redirect_uri=${encodeURIComponent(redirectUri)}&` +
          `state=${state}&` +
          `scope=${encodeURIComponent(scope)}`;
      } else if (provider === 'twitter') {
        const scope = 'tweet.write tweet.read users.read offline.access';
        const codeVerifier = generateCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        sessionStorage.setItem(`oauth_code_verifier_${provider}`, codeVerifier);

        authUrl = `https://x.com/i/oauth2/authorize?` +
          `response_type=code&` +
          `client_id=${encodeURIComponent(clientId)}&` +
          `redirect_uri=${encodeURIComponent(redirectUri)}&` +
          `state=${state}&` +
          `scope=${encodeURIComponent(scope)}&` +
          `code_challenge=${codeChallenge}&` +
          `code_challenge_method=S256`;
      }

      const width = 600;
      const height = 700;
      const left = window.screen?.width / 2 - width / 2;
      const top = window.screen?.height / 2 - height / 2;

      const popup = window.open(
        authUrl,
        `${provider}_oauth`,
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
      );

      if (!popup) {
        toast.error('Pop-up blocked. Please allow pop-ups for this site.');
        setLoading(prev => ({ ...prev, [provider]: false }));
        return;
      }

      let checkPopupClosed = null;
      let messageReceived = false;

      const handleMessage = async (event) => {
        if (event?.origin !== window.location?.origin) return;

        if (event?.data?.type === 'oauth_success' && event?.data?.provider === provider) {
          messageReceived = true;
          if (checkPopupClosed) clearInterval(checkPopupClosed);
          window.removeEventListener('message', handleMessage);
          if (bc) { bc.close(); bc = null; }

          try {
            const { data: { session: freshSession }, error: sessionError } = await supabase?.auth?.getSession();

            if (sessionError || !freshSession?.access_token) {
              throw new Error('Session expired');
            }

            const codeVerifier = sessionStorage.getItem(`oauth_code_verifier_${provider}`);

            const response = await fetch(
              `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/oauth-exchange`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${freshSession?.access_token}`
                },
                body: JSON.stringify({
                  code: event?.data?.code,
                  provider: provider,
                  redirectUri: redirectUri,
                  codeVerifier: codeVerifier
                })
              }
            );

            const result = await response?.json();

            if (!result?.success) {
              throw new Error(result.error || 'Token exchange failed');
            }

            setConnections(prev => ({
              ...prev,
              [provider]: {
                connected: true,
                lastAuth: new Date()?.toISOString(),
                status: 'connected',
                tokenExpiry: result?.expiresAt
              }
            }));

            toast.success(`${provider === 'linkedin' ? 'LinkedIn' : 'X'} connected successfully!`);
            await updateProfile({});

            sessionStorage.removeItem(`oauth_state_${provider}`);
            sessionStorage.removeItem(`oauth_code_verifier_${provider}`);
            sessionStorage.removeItem(`oauth_user_id_${provider}`);
          } catch (error) {
            console.error(`${provider} token exchange error:`, error?.message);
            toast.error(friendlyError(error));
          } finally {
            setLoading(prev => ({ ...prev, [provider]: false }));
          }
        } else if (event?.data?.type === 'oauth_error') {
          messageReceived = true;
          if (checkPopupClosed) clearInterval(checkPopupClosed);
          window.removeEventListener('message', handleMessage);
          if (bc) { bc.close(); bc = null; }
          toast.error(friendlyError(event?.data?.errorDescription || event?.data?.error));
          setLoading(prev => ({ ...prev, [provider]: false }));
          sessionStorage.removeItem(`oauth_user_id_${provider}`);
        }
      };

      window.addEventListener('message', handleMessage);

      // BroadcastChannel fallback — window.opener can be null after cross-origin OAuth redirects
      let bc = null;
      try {
        bc = new BroadcastChannel('oauth_channel');
        bc.onmessage = (e) => handleMessage({ origin: window.location.origin, data: e.data });
      } catch (_) {}

      checkPopupClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkPopupClosed);
          if (!messageReceived) {
            window.removeEventListener('message', handleMessage);
            if (bc) { bc.close(); bc = null; }
            setLoading(prev => ({ ...prev, [provider]: false }));
            toast.warning('Authentication was cancelled.');
          }
        }
      }, 1000);

    } catch (error) {
      console.error(`${provider} OAuth error:`, error?.message);
      toast.error(friendlyError(error));
      setLoading(prev => ({ ...prev, [provider]: false }));
    }
  };

  const generateCodeVerifier = () => {
    const array = new Uint8Array(32);
    crypto?.getRandomValues(array);
    return btoa(String.fromCharCode(...array))
      ?.replace(/\+/g, '-')
      ?.replace(/\//g, '_')
      ?.replace(/=/g, '');
  };

  const generateCodeChallenge = async (verifier) => {
    const encoder = new TextEncoder();
    const data = encoder?.encode(verifier);
    const hash = await crypto?.subtle?.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(hash)))
      ?.replace(/\+/g, '-')
      ?.replace(/\//g, '_')
      ?.replace(/=/g, '');
  };

  const handleDisconnect = async (provider) => {
    setLoading(prev => ({ ...prev, [provider]: true }));
    try {
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();
      if (sessionError || !session?.access_token) throw new Error('Session not found');

      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/oauth-disconnect`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ provider }),
        }
      );
      const result = await response?.json();
      if (!result?.success) throw new Error(result?.error || 'Failed to disconnect');

      setConnections(prev => ({
        ...prev,
        [provider]: { connected: false, lastAuth: null, status: 'disconnected', tokenExpiry: null }
      }));

      toast.success(`${provider === 'linkedin' ? 'LinkedIn' : 'X'} disconnected.`);
      await updateProfile({});
    } catch (error) {
      console.error(`${provider} disconnect error:`, error?.message);
      toast.error(friendlyError(error, 'Failed to disconnect. Please try again.'));
    } finally {
      setLoading(prev => ({ ...prev, [provider]: false }));
    }
  };

  const platforms = [
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: 'Linkedin',
      color: '#0A66C2',
      description: 'Publish professional content directly',
    },
    {
      id: 'twitter',
      name: 'X',
      icon: 'X',
      color: '#A78BFA',
      description: 'Share updates with your audience',
    },
    {
      id: 'bluesky',
      name: 'Bluesky',
      icon: 'Bluesky',
      color: '#0085FF',
      description: 'Reach the decentralized social network',
      comingSoon: true,
    },
    {
      id: 'mastodon',
      name: 'Mastodon',
      icon: 'Mastodon',
      color: '#6364FF',
      description: 'Connect with the open social web',
      comingSoon: true,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <main className="px-4 md:px-6 lg:px-8 pb-12 flex flex-col min-h-[calc(100vh-64px)]">
        <div className="max-w-3xl mx-auto w-full flex-1 flex flex-col justify-center">
          {/* Header — centered like Writing Styles */}
          <div className="text-center mb-10">
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">Link Accounts</h1>
            <p className="text-sm text-muted-foreground">
              Connect your social accounts to publish directly from POSTRequest
            </p>
          </div>

          {/* Platform cards — consistent width */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto mb-8">
            {platforms.map((p) => {
              const conn = connections?.[p.id];
              const isConnected = conn?.connected;
              const isComingSoon = p.comingSoon;

              return (
                <div
                  key={p.id}
                  className={`flex items-center gap-3 px-5 py-4 bg-card border border-border rounded-xl transition-all duration-200 ${
                    isComingSoon ? 'opacity-50' : 'hover:border-muted-foreground'
                  }`}
                >
                  {/* Platform icon */}
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${p.color || '#A78BFA'}15` }}
                  >
                    <Icon name={p.icon} size={18} color={p.color} />
                  </div>

                  {/* Name + description */}
                  <div className="text-left min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-foreground">{p.name}</span>
                      {isConnected && (
                        <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>
                  </div>

                  {/* Action */}
                  <div className="ml-2 flex-shrink-0">
                    {isComingSoon ? (
                      <span className="text-[10px] text-muted-foreground font-medium">Stay tuned</span>
                    ) : isConnected ? (
                      <button
                        onClick={() => handleDisconnect(p.id)}
                        disabled={loading?.[p.id]}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-all disabled:opacity-50"
                      >
                        {loading?.[p.id] ? <Icon name="Loader" size={12} className="animate-spin" /> : <Icon name="Unlink" size={12} />}
                        Disconnect
                      </button>
                    ) : (
                      <button
                        onClick={() => handleConnect(p.id)}
                        disabled={loading?.[p.id]}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg transition-all disabled:opacity-50"
                        style={{ background: `linear-gradient(135deg, ${p.color || '#A78BFA'}, ${p.color || '#A78BFA'}CC)` }}
                      >
                        {loading?.[p.id] ? <Icon name="Loader" size={12} className="animate-spin" /> : <Icon name="Link" size={12} />}
                        Connect
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Security note */}
          <div className="flex justify-center">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Icon name="Shield" size={14} className="text-violet-400" />
              <span>Encrypted & secure. Minimal permissions. Disconnect anytime.</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OAuthIntegrationSetup;
